import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash, ArrowLeft, LogOut } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProperties = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);

  const [formData, setFormData] = useState({
    mls_number: "",
    address: {
      street_number: "",
      street_name: "",
      street_type: "",
      city: "",
      province: "ON",
      postal_code: "",
      country: "Canada"
    },
    price: "",
    bedrooms: "",
    bathrooms: "",
    square_feet: "",
    lot_size: "",
    property_type: "Condo",
    description: "",
    list_date: new Date().toISOString().split('T')[0],
    status: "Active",
    days_on_market: 0,
    features: "",
    images: ""
  });

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchProperties();
  }, [navigate]);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");

    const propertyData = {
      ...formData,
      price: parseFloat(formData.price),
      bedrooms: parseInt(formData.bedrooms),
      bathrooms: parseFloat(formData.bathrooms),
      square_feet: formData.square_feet ? parseInt(formData.square_feet) : null,
      days_on_market: parseInt(formData.days_on_market),
      features: formData.features ? formData.features.split(",").map(f => f.trim()) : [],
      images: formData.images ? formData.images.split("\n").filter(url => url.trim()).map(url => ({ url: url.trim(), description: "" })) : []
    };

    try {
      if (editingProperty) {
        await axios.put(`${API}/admin/properties/${editingProperty.mls_number}`, propertyData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Property updated successfully!");
      } else {
        await axios.post(`${API}/admin/properties`, propertyData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Property added successfully!");
      }
      setDialogOpen(false);
      resetForm();
      fetchProperties();
    } catch (error) {
      toast.error("Failed to save property");
    }
  };

  const handleDelete = async (mlsNumber) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;

    const token = localStorage.getItem("adminToken");
    try {
      await axios.delete(`${API}/admin/properties/${mlsNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Property deleted successfully!");
      fetchProperties();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      mls_number: property.mls_number,
      address: property.address,
      price: property.price.toString(),
      bedrooms: property.bedrooms.toString(),
      bathrooms: property.bathrooms.toString(),
      square_feet: property.square_feet?.toString() || "",
      lot_size: property.lot_size || "",
      property_type: property.property_type,
      description: property.description || "",
      list_date: property.list_date,
      status: property.status,
      days_on_market: property.days_on_market,
      features: property.features?.join(", ") || "",
      images: property.images?.map(img => img.url).join("\n") || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProperty(null);
    setFormData({
      mls_number: "",
      address: {
        street_number: "",
        street_name: "",
        street_type: "",
        city: "",
        province: "ON",
        postal_code: "",
        country: "Canada"
      },
      price: "",
      bedrooms: "",
      bathrooms: "",
      square_feet: "",
      lot_size: "",
      property_type: "Condo",
      description: "",
      list_date: new Date().toISOString().split('T')[0],
      status: "Active",
      days_on_market: 0,
      features: "",
      images: ""
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="admin-properties-page">
      <div className="bg-primary text-primary-foreground py-6">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/admin" className="mr-6">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <span className="text-2xl font-heading font-bold">MURRAY</span>
              <span className="text-2xl font-heading font-light text-accent ml-2">REALTOR</span>
              <span className="ml-4 text-sm uppercase tracking-widest">Manage Properties</span>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-2 border-white text-white hover:bg-white hover:text-primary h-10 px-6 rounded-none uppercase tracking-widest text-sm"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-primary">Property Listings</h1>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 rounded-none uppercase tracking-widest text-sm" data-testid="add-property-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Property
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProperty ? "Edit Property" : "Add New Property"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>MLS Number *</Label>
                    <Input required value={formData.mls_number} onChange={(e) => setFormData({...formData, mls_number: e.target.value})} disabled={!!editingProperty} />
                  </div>
                  <div>
                    <Label>Property Type *</Label>
                    <select value={formData.property_type} onChange={(e) => setFormData({...formData, property_type: e.target.value})} className="w-full h-10 px-3 border border-border rounded">
                      <option>Condo</option>
                      <option>Detached</option>
                      <option>Semi-detached</option>
                      <option>Townhouse</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Street Number *</Label>
                    <Input required value={formData.address.street_number} onChange={(e) => setFormData({...formData, address: {...formData.address, street_number: e.target.value}})} />
                  </div>
                  <div>
                    <Label>Street Name *</Label>
                    <Input required value={formData.address.street_name} onChange={(e) => setFormData({...formData, address: {...formData.address, street_name: e.target.value}})} />
                  </div>
                  <div>
                    <Label>Street Type *</Label>
                    <Input required value={formData.address.street_type} onChange={(e) => setFormData({...formData, address: {...formData.address, street_type: e.target.value}})} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input required value={formData.address.city} onChange={(e) => setFormData({...formData, address: {...formData.address, city: e.target.value}})} />
                  </div>
                  <div>
                    <Label>Province *</Label>
                    <Input required value={formData.address.province} onChange={(e) => setFormData({...formData, address: {...formData.address, province: e.target.value}})} />
                  </div>
                  <div>
                    <Label>Postal Code *</Label>
                    <Input required value={formData.address.postal_code} onChange={(e) => setFormData({...formData, address: {...formData.address, postal_code: e.target.value}})} />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Price *</Label>
                    <Input required type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} />
                  </div>
                  <div>
                    <Label>Bedrooms *</Label>
                    <Input required type="number" value={formData.bedrooms} onChange={(e) => setFormData({...formData, bedrooms: e.target.value})} />
                  </div>
                  <div>
                    <Label>Bathrooms *</Label>
                    <Input required type="number" step="0.5" value={formData.bathrooms} onChange={(e) => setFormData({...formData, bathrooms: e.target.value})} />
                  </div>
                  <div>
                    <Label>Square Feet</Label>
                    <Input type="number" value={formData.square_feet} onChange={(e) => setFormData({...formData, square_feet: e.target.value})} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Lot Size</Label>
                    <Input value={formData.lot_size} onChange={(e) => setFormData({...formData, lot_size: e.target.value})} placeholder="e.g., 50 x 120 ft" />
                  </div>
                  <div>
                    <Label>List Date *</Label>
                    <Input required type="date" value={formData.list_date} onChange={(e) => setFormData({...formData, list_date: e.target.value})} />
                  </div>
                  <div>
                    <Label>Status *</Label>
                    <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full h-10 px-3 border border-border rounded">
                      <option>Active</option>
                      <option>Sold</option>
                      <option>Pending</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea rows={4} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
                </div>

                <div>
                  <Label>Features (comma-separated)</Label>
                  <Input value={formData.features} onChange={(e) => setFormData({...formData, features: e.target.value})} placeholder="e.g., Pool, Gym, Parking" />
                </div>

                <div>
                  <Label>Image URLs (one per line)</Label>
                  <Textarea rows={4} value={formData.images} onChange={(e) => setFormData({...formData, images: e.target.value})} placeholder="https://example.com/image1.jpg" />
                </div>

                <div className="flex gap-4">
                  <Button type="submit" className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90">
                    {editingProperty ? "Update Property" : "Add Property"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">Loading properties...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <Card key={property.mls_number} data-testid={`property-${property.mls_number}`}>
                <CardContent className="p-6">
                  <div className="mb-4">
                    {property.images && property.images.length > 0 && (
                      <img src={property.images[0].url} alt={property.address.street_name} className="w-full h-48 object-cover mb-4" />
                    )}
                    <h3 className="text-xl font-heading font-semibold text-primary mb-2">
                      {property.address.street_number} {property.address.street_name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">{property.address.city}, {property.address.province}</p>
                    <p className="text-2xl font-bold text-accent mb-2">{formatPrice(property.price)}</p>
                    <p className="text-sm text-muted-foreground">MLS #{property.mls_number}</p>
                    <p className="text-sm text-muted-foreground">{property.bedrooms} bed | {property.bathrooms} bath</p>
                    <span className={`inline-block mt-2 px-2 py-1 text-xs ${property.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {property.status}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleEdit(property)} variant="outline" size="sm" className="flex-1" data-testid={`edit-${property.mls_number}`}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button onClick={() => handleDelete(property.mls_number)} variant="outline" size="sm" className="flex-1 text-red-600 hover:text-red-700" data-testid={`delete-${property.mls_number}`}>
                      <Trash className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProperties;
